import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { useTenant } from '../context/TenantContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, 
  Puzzle, 
  Check, 
  Crown, 
  ShieldCheck, 
  Zap,
  ArrowRight,
  Loader2,
  X,
  Mail
} from 'lucide-react';

const ICON_MAP = {
  'file-spreadsheet': FileSpreadsheet,
  'puzzle': Puzzle,
  'shield-check': ShieldCheck,
  'zap': Zap,
};

const Addons = () => {
  const { t, i18n } = useTranslation();
  const { API, token } = useContext(AuthContext);
  const { tenant } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [planCode, setPlanCode] = useState('free');
  const [showConfirm, setShowConfirm] = useState(null);
  const lang = i18n.language;

  useEffect(() => {
    fetchAddons();
    // Handle return from payment page
    if (searchParams.get('success') === 'true') {
      toast.success(t('addons.purchaseSuccess'));
      // Clean up URL params
      setSearchParams({}, { replace: true });
    }
  }, []);

  const fetchAddons = async () => {
    try {
      const res = await axios.get(`${API}/addons/catalog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddons(res.data.addons || []);
      setPlanCode(res.data.plan_code || 'free');
    } catch (err) {
      console.error('Failed to fetch addons:', err);
      toast.error(t('addons.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (addonId, billingCycle = 'monthly') => {
    setPurchasing(addonId);
    try {
      const res = await axios.post(`${API}/addons/checkout`, 
        { 
          addon_id: addonId, 
          billing_cycle: billingCycle,
          return_url: window.location.origin 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.checkout_url) {
        // Redirect to payment page
        window.location.href = res.data.checkout_url;
      } else {
        toast.error(t('addons.purchaseError'));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object') {
        toast.error(detail.message || t('addons.purchaseError'));
      } else {
        toast.error(detail || t('addons.purchaseError'));
      }
      setPurchasing(null);
      setShowConfirm(null);
    }
  };

  const handleCancel = async (addonId) => {
    setCancelling(addonId);
    try {
      await axios.post(`${API}/addons/cancel`,
        { addon_id: addonId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('addons.cancelSuccess'));
      fetchAddons();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('addons.cancelError'));
    } finally {
      setCancelling(null);
    }
  };

  const formatPrice = (priceInKurus) => {
    if (!priceInKurus) return t('addons.free');
    return `₺${(priceInKurus / 100).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')}`;
  };

  const canUpgrade = planCode === 'free' || planCode === 'starter';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Puzzle className="h-7 w-7 text-purple-600" />
          {t('addons.title')}
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          {t('addons.subtitle')}
        </p>
      </div>

      {/* Upgrade Banner for Free users */}
      {canUpgrade && (
        <div className="mb-8 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Crown className="h-5 w-5" />
                {t('addons.upgradeTitle')}
              </h3>
              <p className="text-purple-100 mt-1">{t('addons.upgradeDesc')}</p>
            </div>
            <a
              href="/admin/upgrade"
              className="px-6 py-2.5 bg-white text-purple-700 font-semibold rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2"
            >
              {t('addons.upgradeCta')} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}

      {/* Add-ons Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {addons.map((addon) => {
          const IconComponent = ICON_MAP[addon.icon] || Puzzle;
          const isOwned = addon.is_owned;
          const canBuy = addon.can_purchase && !isOwned;
          const isEnterpriseTier = addon.tier === 'enterprise';
          const name = lang === 'tr' ? (addon.name_tr || addon.name) : addon.name;
          const description = lang === 'tr' ? (addon.description_tr || addon.description) : addon.description;
          const features = lang === 'tr' ? (addon.features_tr || addon.features) : addon.features;

          return (
            <div
              key={addon.id}
              className={`relative bg-white dark:bg-gray-800 rounded-2xl border-2 overflow-hidden transition-all hover:shadow-lg ${
                isOwned
                  ? 'border-green-300 dark:border-green-600 shadow-green-50'
                  : isEnterpriseTier
                    ? 'border-amber-200 dark:border-amber-700'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
              }`}
            >
              {/* Tier Badge */}
              <div className="absolute top-4 right-4">
                {isOwned ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                    <Check className="h-3 w-3" /> {t('addons.active')}
                  </span>
                ) : isEnterpriseTier ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                    <Crown className="h-3 w-3" /> Enterprise
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">
                    <Zap className="h-3 w-3" /> Pro
                  </span>
                )}
              </div>

              <div className="p-6">
                {/* Icon & Name */}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-3 rounded-xl ${isOwned ? 'bg-green-100' : 'bg-purple-100'}`}>
                    <IconComponent className={`h-6 w-6 ${isOwned ? 'text-green-600' : 'text-purple-600'}`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{name}</h3>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{description}</p>

                {/* Features List */}
                <ul className="space-y-2 mb-6">
                  {(features || []).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Price */}
                {!isEnterpriseTier && (
                  <div className="mb-4">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatPrice(addon.price_monthly)}
                    </span>
                    <span className="text-sm text-gray-500">/{t('addons.month')}</span>
                    {addon.price_annual > 0 && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {t('addons.orAnnual', { price: formatPrice(addon.price_annual) })}
                      </div>
                    )}
                  </div>
                )}

                {/* Action Button */}
                {isOwned ? (
                  <button
                    onClick={() => handleCancel(addon.id)}
                    disabled={cancelling === addon.id}
                    className="w-full py-2.5 border-2 border-red-200 text-red-600 font-medium rounded-xl hover:bg-red-50 transition-colors text-sm disabled:opacity-50"
                  >
                    {cancelling === addon.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    ) : (
                      t('addons.cancel')
                    )}
                  </button>
                ) : canBuy ? (
                  showConfirm === addon.id ? (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-600 dark:text-gray-400 text-center mb-2">
                        {t('addons.confirmPurchase')}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePurchase(addon.id, 'monthly')}
                          disabled={purchasing === addon.id}
                          className="flex-1 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors text-sm disabled:opacity-50"
                        >
                          {purchasing === addon.id ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t('addons.monthly')}
                        </button>
                        {addon.price_annual > 0 && (
                          <button
                            onClick={() => handlePurchase(addon.id, 'annual')}
                            disabled={purchasing === addon.id}
                            className="flex-1 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
                          >
                            {purchasing === addon.id ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t('addons.annual')}
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => setShowConfirm(null)}
                        className="w-full py-1.5 text-gray-500 text-xs hover:text-gray-700"
                      >
                        {t('addons.cancelAction')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowConfirm(addon.id)}
                      className="w-full py-2.5 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                    >
                      {t('addons.purchase')} <ArrowRight className="h-4 w-4" />
                    </button>
                  )
                ) : isEnterpriseTier ? (
                  <a
                    href="/contact"
                    className="w-full py-2.5 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Mail className="h-4 w-4" /> {t('addons.contactUs')}
                  </a>
                ) : (
                  <a
                    href="/admin/upgrade"
                    className="w-full py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <Crown className="h-4 w-4" /> {t('addons.upgradeRequired')}
                  </a>
                )}
              </div>
            </div>
          );
        })}

        {/* Placeholder: Coming Soon card */}
        <div className="relative bg-gray-50 dark:bg-gray-800/50 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden">
          <div className="p-6 flex flex-col items-center justify-center h-full min-h-[300px] text-center">
            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-xl mb-4">
              <Puzzle className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-500 dark:text-gray-400 mb-2">
              {t('addons.moreComingSoon')}
            </h3>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              {t('addons.moreComingSoonDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Addons;
